import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../auth.jsx'
import AuthShell, { AuthField, AuthPasswordField, AuthButton, AuthError } from '../components/AuthShell.jsx'

export default function Login() {
  const { login, isCognito } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // Invited users must set a password on first sign-in (Cognito challenge).
  const [challenge, setChallenge] = useState(null)
  const [newPassword, setNewPassword] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (!email.trim() || !password.trim()) {
      setError('Enter an email and password to continue.')
      return
    }
    setBusy(true)
    try {
      const result = await login(email.trim(), password)
      if (result.status === 'NEW_PASSWORD_REQUIRED') {
        setChallenge(result)
      } else {
        navigate('/', { replace: true })
      }
    } catch (err) {
      setError(err.message || 'Sign in failed.')
    } finally {
      setBusy(false)
    }
  }

  async function handleSetPassword(e) {
    e.preventDefault()
    setError('')
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    setBusy(true)
    try {
      await challenge.setPassword(newPassword)
      navigate('/', { replace: true })
    } catch (err) {
      setError(err.message || 'Could not set password.')
    } finally {
      setBusy(false)
    }
  }

  if (challenge) {
    return (
      <AuthShell
        title="Set a password"
        subline={<>welcome — finish activating your account</>}
      >
        <form onSubmit={handleSetPassword} className="flex flex-col gap-y-3">
          <AuthPasswordField
            label="New password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            placeholder="At least 8 characters"
            autoComplete="new-password"
            autoFocus
          />
          <AuthError>{error}</AuthError>
          <AuthButton disabled={busy}>{busy ? 'Saving…' : 'Enter'}</AuthButton>
        </form>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Sign in"
      subline={
        <>
          or <Link to="/signup">create an account</Link>
        </>
      }
      below={
        <>
          <Link
            to="/forgot-password"
            className="text-[13.5px] text-[#8f8f8f] no-underline transition-colors hover:text-[#cfcfcf]"
          >
            Forgot password?
          </Link>
          {!isCognito && (
            <div className="mt-6 text-[12px] text-[#5c5c5c]">
              Demo login — any email &amp; password will work.
            </div>
          )}
        </>
      }
    >
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <AuthField
          label="Email or username"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@company.com"
          autoComplete="username"
          autoFocus
        />
        <AuthPasswordField
          label="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="••••••••••••"
          autoComplete="current-password"
        />
        <AuthError>{error}</AuthError>
        <AuthButton disabled={busy}>{busy ? 'Signing in…' : 'Enter'}</AuthButton>
      </form>
    </AuthShell>
  )
}
